<template>
  <PageChrome :show-header="false">
    <div v-if="play" class="canvasPage">
      <SalesPlayHeader
        :play-id="playId"
        :play="play"
        :agent-run-counts="{ running: 0, queued: 0 }"
        :refresh-play="() => fetchPlay(true)"
        @stage-filter="(stage, kind) => $router.push(`/sales-plays/${playId}/candidates/${kind === 'account' ? 'companies' : 'people'}${stage ? `?stage=${stage}` : ''}`)"
      />

      <SalesPlayTabBar :play-id="playId" :engine="play?.engine" />

      <StrategyPlayView v-if="isFlock" :play-id="playId" />

      <!-- ── Canvas flow ─────────────────────────────────────── -->
      <div class="canvasBg" @click.self="deselectNode">

        <!-- Mode bar: Edit button (run mode) or Save/Cancel (edit mode) -->
        <div class="modeBar">
          <template v-if="mode === 'run'">
            <button class="btn secondary" type="button" @click="enterEditMode">
              <AppIcon name="pencil" :size="14" />
              <span>Edit</span>
            </button>
            <router-link v-if="!isFlock" class="btn secondary" :to="`/sales-plays/${playId}/settings`">
              <span>Settings</span>
            </router-link>
            <router-link class="btn secondary" :to="`/sales-plays/${playId}/versions`">
              <span>Versions</span>
            </router-link>
            <button v-if="!isFlock" class="btn secondary" type="button" @click="onExport">
              <span>Export</span>
            </button>
            <button v-if="!isFlock" class="btn primary" type="button" :disabled="play?.status === 'archived'" @click="onTogglePause">
              <span>{{ play?.status === 'paused' ? 'Resume' : 'Pause' }}</span>
            </button>
          </template>
          <template v-else>
            <button
              class="btn secondary"
              type="button"
              @click="onCancel"
            >
              <span>Cancel</span>
            </button>
            <button
              class="btn primary"
              type="button"
              :disabled="!dirty || saving || previewing"
              @click="onSave"
            >
              <span>{{ saving ? 'Saving\u2026' : previewing ? 'Checking\u2026' : 'Save' }}</span>
            </button>
          </template>
        </div>
        <p v-if="previewError" class="saveErrorBanner">{{ previewError }}</p>

      <div class="canvasFlow" @click.self="deselectNode">
        <!-- Start pill -->
        <div v-if="!isFlock" class="startPill">start</div>

        <!-- ════════════════════════════════════════════════════ -->
        <!-- ── RUN MODE ─────────────────────────────────────── -->
        <!-- ════════════════════════════════════════════════════ -->
        <template v-if="mode === 'run'">
          <template v-for="(group, gi) in runGroups" :key="gi">
            <!-- Gap before this group -->
            <div v-if="!isFlock" class="connector">
              <ConnectorBadge
                v-if="runGaps[gi]?.show"
                :entries="runGaps[gi]?.entries"
              />
            </div>

            <!-- ── Filter group ── -->
            <template v-if="group.kind === 'filterGroup'">
              <FilterGroupBox v-if="group.nodes.length === 0" label="Qualify">
                <p class="emptyGroupHint">{{ RUN_GROUP_HINTS.filterGroup }}</p>
              </FilterGroupBox>
              <FilterGroupBox v-else label="Qualify" :running="filterRunning">
                <template v-for="(node, fi) in group.nodes" :key="node.id">
                  <!-- Connector between filter cards (with badge) -->
                  <template v-if="fi > 0">
                    <div class="connector">
                      <ConnectorBadge :count="getFilterIntermediateCount(fi)" />
                    </div>
                  </template>
                  <SalesPlayCanvasCard
                    :icon="node.icon"
                    :title="node.title"
                    :icon-variant="node.kind"
                    :operates-on="node.operatesOn"
                    :readonly="true"
                    @select="onRunNodeClick(node)"
                  >
                    <template #subtitle>{{ node.subtitle }}</template>
                    <template v-if="node.detailsComponent" #details>
                      <component :is="node.detailsComponent" v-bind="node.detailsProps" />
                    </template>
                  </SalesPlayCanvasCard>
                </template>

                <template #footer>
                  <RunNodeFooter
                    :show-run-now="true"
                    :show-auto-run="true"
                    :running="filterRunning"
                    :record-limit="filterRecordLimit"
                    :auto-run="filterAutoRun"
                    :last-run-summary="filterLastRunSummary"
                    :run-counts="filterRunCounts"
                    @run="onRunFilters"
                    @update:record-limit="filterRecordLimit = $event"
                    @update:auto-run="onToggleFilterAutoRun"
                  />
                </template>
              </FilterGroupBox>
            </template>

            <!-- ── Action group (sequence + dialer) ── -->
            <template v-else-if="group.kind === 'actionGroup'">
              <FilterGroupBox v-if="group.nodes.length === 0" label="Actions">
                <template #corner-badge>
                  <router-link
                    class="cornerCount cornerCount--link"
                    :to="`/sales-plays/${playId}/candidates/people?stage=active`"
                  >
                    <AppIcon name="contact" :size="13" />
                    {{ runStats.activeContactCount ?? 0 }}
                  </router-link>
                </template>
                <p class="emptyGroupHint">{{ isFlock ? STRATEGY_NO_STEPS_HINT : RUN_GROUP_HINTS.actionGroup }}</p>
              </FilterGroupBox>
              <FilterGroupBox v-else label="Actions">
                <template #corner-badge>
                  <router-link
                    class="cornerCount cornerCount--link"
                    :to="`/sales-plays/${playId}/candidates/people?stage=active`"
                  >
                    <AppIcon name="contact" :size="13" />
                    {{ runStats.activeContactCount ?? 0 }}
                  </router-link>
                </template>
                <template v-for="(node, ai) in group.nodes" :key="node.id">
                  <!-- Connector between action cards -->
                  <template v-if="ai > 0">
                    <div class="connector">
                      <ConnectorBadge :count="null" />
                    </div>
                  </template>
                  <SalesPlayCanvasCard
                    :icon="node.icon"
                    :title="node.title"
                    :icon-variant="node.kind"
                    :readonly="true"
                    @select="onRunNodeClick(node)"
                  >
                    <template #subtitle>{{ node.subtitle }}</template>
                    <template v-if="node.type === 'sequence' || node.type === 'dialer'" #count-badge>
                      <AppIcon name="contact" :size="13" />
                      {{ actionInProgressCount(node.type === 'sequence' ? sequenceActionId : dialerActionId) }}
                    </template>
                    <template v-if="node.detailsComponent" #details>
                      <component :is="node.detailsComponent" v-bind="node.detailsProps" />
                    </template>
                    <!-- Sequence's own auto-approve policy stays on its card —
                         it governs THIS node's reachouts, not the play-wide
                         action-phase auto-run policy. -->
                    <template v-if="node.type === 'sequence'" #footer>
                      <RunNodeFooter
                        :show-auto-approve="!isFlock"
                        :show-approval-warning="workspaceRequiresApproval && sequenceAutoApprovePolicy !== 'none'"
                        :auto-approve-policy="sequenceAutoApprovePolicy"
                        :pending-reachouts-count="play?.pending_reachouts_count || 0"
                        :run-counts="sequenceRunCounts"
                        :action-id="sequenceActionId"
                        :action-run-counts="sequenceActionId ? actionRunCounts[sequenceActionId] : null"
                        :play-id="playId"
                        @update:auto-approve-policy="onChangeSequenceAutoApprovePolicy"
                        @open-needs-attention="onRunNodeClick(node, 'needs_attention')"
                      />
                    </template>
                    <template v-else-if="node.type === 'dialer'" #footer>
                      <RunNodeFooter
                        :run-counts="dialerRunCounts"
                        :action-id="dialerActionId"
                        :action-run-counts="dialerActionId ? actionRunCounts[dialerActionId] : null"
                        :play-id="playId"
                        @open-needs-attention="onRunNodeClick(node, 'needs_attention')"
                      />
                    </template>
                  </SalesPlayCanvasCard>
                </template>

                <template #footer>
                  <RunNodeFooter
                    :show-run-now="!isFlock"
                    :show-auto-run="!isFlock"
                    :running="actionRunning"
                    :record-limit="actionRecordLimit"
                    :auto-run="actionPhaseAutoRun"
                    @run="onRunActions"
                    @update:record-limit="actionRecordLimit = $event"
                    @update:auto-run="onToggleActionPhaseAutoRun"
                  />
                </template>
              </FilterGroupBox>
            </template>

            <!-- ── Source group (Find) — one shared box/label, but each
                 source runs and auto-runs fully independently, so (unlike
                 filters/actions) there is no shared group-level footer;
                 every card keeps its own. ── -->
            <template v-else-if="group.kind === 'sourceGroup'">
              <FilterGroupBox v-if="group.nodes.length === 0" label="Find">
                <p class="emptyGroupHint">{{ RUN_GROUP_HINTS.sourceGroup }}</p>
              </FilterGroupBox>
              <FilterGroupBox v-else label="Find">
                <template v-for="(node, si) in group.nodes" :key="node.id">
                  <!-- Connector between source cards -->
                  <template v-if="si > 0">
                    <div class="connector">
                      <ConnectorBadge :count="null" />
                    </div>
                  </template>
                  <SalesPlayCanvasCard
                    :icon="node.icon"
                    :title="node.title"
                    :icon-variant="node.kind"
                    :readonly="true"
                    @select="onRunNodeClick(node)"
                  >
                    <template #subtitle>{{ node.subtitle }}</template>
                    <template v-if="node.detailsComponent" #details>
                      <component :is="node.detailsComponent" v-bind="node.detailsProps" />
                    </template>
                    <template #footer>
                      <RunNodeFooter
                        :show-run-now="true"
                        :show-auto-run="true"
                        :running="!!sourceRunningById[node.id]"
                        :record-limit="sourceRecordLimitFor(node.id)"
                        :auto-run="!!sourceAutoRunById[node.id]"
                        :last-run-summary="sourceLastRunSummaryFor(node.detailsProps.config.id)"
                        :latest-run="latestSourceRunFor(node.detailsProps.config.id)"
                        :cadence-label="sourceCadenceLabelFor(node.id)"
                        @run="onRunSource(node.id, node.detailsProps.config.id)"
                        @update:record-limit="sourceRecordLimitById = { ...sourceRecordLimitById, [node.id]: $event }"
                        @update:auto-run="onToggleSourceAutoRun(node.id, node.detailsProps.config.id, $event)"
                      />
                    </template>
                  </SalesPlayCanvasCard>
                </template>
              </FilterGroupBox>
            </template>

            <!-- ── Single node (prospect) ── -->
            <template v-else>
              <FilterGroupBox v-if="!group.node" label="Prospect">
                <template #corner-badge>
                  <router-link
                    class="cornerCount cornerCount--link"
                    :to="`/sales-plays/${playId}/candidates/companies?stage=active`"
                  >
                    <AppIcon name="account" :size="13" />
                    {{ runStats.activeAccountCount ?? 0 }}
                  </router-link>
                </template>
                <p class="emptyGroupHint">{{ RUN_GROUP_HINTS.single }}</p>
              </FilterGroupBox>
              <FilterGroupBox v-else label="Prospect">
                <template #corner-badge>
                  <router-link
                    class="cornerCount cornerCount--link"
                    :to="`/sales-plays/${playId}/candidates/companies?stage=active`"
                  >
                    <AppIcon name="account" :size="13" />
                    {{ runStats.activeAccountCount ?? 0 }}
                  </router-link>
                </template>
                <SalesPlayCanvasCard
                  :icon="group.node.icon"
                  :title="group.node.title"
                  :icon-variant="group.node.kind"
                  :readonly="true"
                  @select="onRunNodeClick(group.node)"
                >
                  <template #subtitle>{{ group.node.subtitle }}</template>
                  <template v-if="group.node.detailsComponent" #details>
                    <component :is="group.node.detailsComponent" v-bind="group.node.detailsProps" />
                  </template>
                  <template #footer>
                    <RunNodeFooter
                      :show-run-now="true"
                      :show-auto-run-mode="true"
                      :running="prospectRunning"
                      :record-limit="prospectRecordLimit"
                      :auto-run-mode="prospectAutoRunMode"
                      :last-run-summary="prospectLastRunSummary"
                      :run-counts="prospectRunCounts"
                      :limit-options="prospectLimitOptions"
                      @run="onRunProspect"
                      @update:record-limit="prospectRecordLimit = $event"
                      @update:auto-run-mode="onSetProspectAutoRunMode"
                    />
                  </template>
                </SalesPlayCanvasCard>
              </FilterGroupBox>
            </template>
          </template>
          <!-- Trailing gap after the last group -->
          <div v-if="runGaps[runGroups.length]?.show" class="connectorTrailing">
            <ConnectorBadge
              :entries="runGaps[runGroups.length]?.entries"
            />
          </div>
        </template>

        <!-- ════════════════════════════════════════════════════ -->
        <!-- ── EDIT MODE ────────────────────────────────────── -->
        <!-- ════════════════════════════════════════════════════ -->
        <template v-else>
          <template v-for="group in editGroups" :key="group.kind">
            <div v-if="!isFlock" class="connector"></div>

            <FilterGroupBox :label="group.label" :dashed="group.nodes.length === 0">
              <template v-for="(entry, ni) in group.nodes" :key="entry.node.id">
                <!-- Plain connector line between cards (no insertion point —
                     reordering via the card's own up/down arrows is how you
                     fix position, since a mid-list (+) is hard to place well). -->
                <div v-if="ni > 0" class="connector"></div>
                <SalesPlayCanvasCard
                  :icon="entry.node.icon"
                  :title="entry.node.title"
                  :icon-variant="entry.node.kind"
                  :operates-on="entry.node.operatesOn"
                  :selected="selectedNodeIndex === entry.index"
                  :title-readonly="isUnnamedNode(entry.node)"
                  :can-move-up="ni > 0"
                  :can-move-down="ni < group.nodes.length - 1"
                  @select="selectNode(entry.index)"
                  @remove="removeNode(entry.index)"
                  @rename="onRenameNode(entry.index, $event)"
                  @move-up="moveNode(entry.index, 'up')"
                  @move-down="moveNode(entry.index, 'down')"
                >
                  <template #subtitle>{{ entry.node.subtitle }}</template>
                  <template v-if="entry.node.detailsComponent" #details>
                    <component :is="entry.node.detailsComponent" v-bind="entry.node.detailsProps" />
                  </template>
                </SalesPlayCanvasCard>
              </template>

              <!-- Trailing add — appends to the end of this group. Only
                   insertion point in the group; centered when the group
                   is empty, otherwise below the last card. Hidden entirely
                   once nothing more can be added here (e.g. Prospect after
                   its one node exists) rather than showing a dead button. -->
              <template v-if="addMenuItemsFor(group.kind).length > 0 || group.nodes.length === 0">
                <div v-if="group.nodes.length > 0" class="connector"></div>
                <div class="addNodeWrap" :class="{ addNodeWrap__centered: group.nodes.length === 0 }">
                  <button
                    class="addNodeBtn"
                    type="button"
                    @click="openAddMenuFor(group.kind, null)"
                    :ref="(el) => { addBtnRefs[`${group.kind}:end`] = el as HTMLElement }"
                  >
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8h10M8 3v10" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                  </button>
                  <div
                    v-if="addMenuOpenFor?.kind === group.kind && addMenuOpenFor?.insertAt === null"
                    class="addMenu"
                    :class="addMenuOpenFor.side === 'left' ? 'addMenu--left' : 'addMenu--right'"
                    :ref="(el) => { addMenuRefs[`${group.kind}:end`] = el as HTMLElement }"
                  >
                    <template v-if="addMenuItemsFor(group.kind).length === 0">
                      <div class="addMenuEmpty">No more node types available.</div>
                    </template>
                    <template v-for="section in addMenuSectionsFor(group.kind)" :key="section.label || '_'">
                      <div v-if="section.label" class="addMenuCategory">{{ section.label }}</div>
                      <button
                        v-for="def in section.items"
                        :key="def.type"
                        class="addMenuItem"
                        type="button"
                        @click="addNodeToGroup(def, null)"
                      >
                        <div class="addMenuItemIcon" :class="`addMenuItemIcon--${def.kind}`">
                          <AppIcon :name="def.addMenu.icon" :size="22" />
                        </div>
                        <div class="addMenuItemText">
                          <div class="addMenuItemTitle">{{ def.addMenu.label }}</div>
                          <div class="addMenuItemDesc">{{ def.addMenu.description }}</div>
                        </div>
                      </button>
                    </template>
                  </div>
                </div>
              </template>
            </FilterGroupBox>
          </template>
        </template>

      </div>
      </div><!-- /canvasBg -->

      <Modal
        v-if="sequenceImpactOpen"
        title="This will affect enrolled candidates"
        :max-width="440"
        @close="onCancelSequenceImpact"
      >
        <p class="sequenceImpactText">
          <strong>{{ sequenceImpactCounts?.affectedCount }}</strong>
          candidate{{ sequenceImpactCounts?.affectedCount === 1 ? '' : 's' }} already
          past a step you changed. Saving will exit them from the sequence —
          you can optionally re-enroll them under the new version instead.
          <span v-if="sequenceImpactCounts?.unaffectedCount">
            {{ sequenceImpactCounts.unaffectedCount }} other candidate{{ sequenceImpactCounts.unaffectedCount === 1 ? '' : 's' }}
            {{ sequenceImpactCounts.unaffectedCount === 1 ? 'hasn’t' : 'haven’t' }} reached a changed step and won't be affected.
          </span>
        </p>
        <template #footer-start>
          <button class="btn secondary" type="button" :disabled="sequenceImpactBusy" @click="onCancelSequenceImpact">
            Cancel
          </button>
        </template>
        <template #footer>
          <button
            class="btn secondary"
            type="button"
            :disabled="sequenceImpactBusy"
            @click="onConfirmSequenceImpact('exit')"
          >
            Exit them
          </button>
          <button
            class="btn primary"
            type="button"
            :disabled="sequenceImpactBusy"
            @click="onConfirmSequenceImpact('exit_and_reenroll')"
          >
            Exit and re-enroll
          </button>
        </template>
      </Modal>
    </div>

    <div v-else-if="loading" class="loading">Loading play...</div>
    <div v-else class="error">{{ error || 'Play not found.' }}</div>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import SalesPlayTabBar from '../components/SalesPlayTabBar.vue'
import SalesPlayHeader from '../components/SalesPlayHeader.vue'
import AppIcon from '../../components/AppIcon.vue'
import SalesPlayCanvasCard from '../sales-play/canvas/SalesPlayCanvasCard.vue'
import ConnectorBadge from '../sales-play/canvas/ConnectorBadge.vue'
import RunNodeFooter from '../sales-play/canvas/RunNodeFooter.vue'
import FilterGroupBox from '../sales-play/canvas/FilterGroupBox.vue'
import Modal from '../components/Modal.vue'
import StrategyPlayView from '../sales-play/strategy/StrategyPlayView.vue'
import { apiFetch } from '../../services/api'
import { useChatScope } from '../composables/useChatScope'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { CANVAS_CONTEXT_TYPE } from '../composables/salesPlayCanvasContext'
import { useSalesPlayDraft } from '../composables/useSalesPlayDraft'
import { useSalesPlayActions } from '../composables/useSalesPlayActions'
import { STRATEGY_STEP_TYPES, usePlayEngine, type PlayEngine } from '../composables/usePlayEngine'
import {
  availableCanvasNodeDefs,
  availableCanvasNodeDefsForKind,
  getCanvasNodeDef,
  initBlockRegistry,
  resolveCanvasNodes,
  type CanvasNode,
  type CanvasNodeDefinition,
  type ResolvedCanvasNode,
} from '../composables/salesPlayNodeRegistry'
import type { CanvasNodeKind } from '../composables/salesPlayCanvasContext'

// ── Types ───────────────────────────────────────────────────────

interface PlaySummary {
  id: string
  name: string
  description: string | null
  status: string
  engine?: PlayEngine
  created_by: { id: string; email: string; name: string } | null
  pipelines: { id: string; audience_definition?: any }[]
  pending_review_count?: number
  staged_account_count?: number
  staged_contact_count?: number
  qualified_count?: number
  qualified_account_count?: number
  qualified_contact_count?: number
  active_count?: number
  enrolled_account_count?: number
  enrolled_contact_count?: number
  engaged_count?: number
  timed_out_count?: number
  disqualified_count?: number
  pending_reachouts_count?: number
  pending_human_dials_count?: number
  agent?: { id: string; name: string; approach: string | null; ready?: boolean } | null
  sequence?: { id: string; name: string; current_version: number; ready?: boolean } | null
  personas?: { local: any[]; referenced: any[] }
  sample_emails?: { id: string; body: string }[]
}

interface RunGroupSingle {
  kind: 'single'
  node: ResolvedCanvasNode | null
}

interface RunGroupSources {
  kind: 'sourceGroup'
  nodes: ResolvedCanvasNode[]
}

interface RunGroupFilters {
  kind: 'filterGroup'
  nodes: ResolvedCanvasNode[]
}

interface RunGroupActions {
  kind: 'actionGroup'
  nodes: ResolvedCanvasNode[]
}

type RunGroup = RunGroupSingle | RunGroupSources | RunGroupFilters | RunGroupActions

/** Fixed hint shown inside a phase box in run mode when it has no nodes yet. */
const RUN_GROUP_HINTS: Record<RunGroup['kind'], string> = {
  sourceGroup: 'No Find configured (I will stage my own candidates)',
  filterGroup: 'No Qualify configured (my candidates are already qualified)',
  single: 'No Prospecting configured (I already have the contacts I need at each account)',
  actionGroup: 'No Actions configured (I am only building a candidate list)',
}
const STRATEGY_NO_STEPS_HINT = 'No steps yet'

/** One fixed group per canvas section, always present in edit mode
 *  (even with zero nodes) so the add-menu can be scoped to "this group"
 *  rather than a single global insertion index. */
interface EditGroup {
  kind: CanvasNodeKind
  label: string
  nodes: { node: ResolvedCanvasNode; index: number }[]
}

// ── Route & scope ───────────────────────────────────────────────

const route = useRoute()
const { setScope, clearScope } = useChatScope()
const { setContext, openDrawer } = useContextDrawer()
const draft = useSalesPlayDraft()

const play = ref<PlaySummary | null>(null)
const { isFlock } = usePlayEngine(play)
const loading = ref(true)
const error = ref('')

const playId = computed(() => String(route.params.id || ''))

const { onExport, onTogglePause } = useSalesPlayActions(playId, play, () => fetchPlay(true))

// ── Mode state ──────────────────────────────────────────────────

const mode = ref<'run' | 'edit'>('run')

function enterEditMode() {
  mode.value = 'edit'
}

// ── Run mode state ──────────────────────────────────────────────

// Keyed by the canvas node id (`source-${source.id}`) — any number of
// source nodes can exist, each running independently on its own record
// limit / auto-run / cadence, so this can't be a single shared ref.
const sourceRecordLimitById = ref<Record<string, number | null>>({})
const sourceAutoRunById = ref<Record<string, boolean>>({})
const sourceCadenceSecondsById = ref<Record<string, number | null>>({})
const sourceRunningById = ref<Record<string, boolean>>({})

function sourceRecordLimitFor(nodeId: string) {
  return sourceRecordLimitById.value[nodeId] ?? 10
}

function _formatCadence(seconds: number): string {
  if (seconds < 60) return `Every ${seconds}s`
  if (seconds < 3600) return `Every ${Math.round(seconds / 60)}m`
  if (seconds < 86400) return `Every ${Math.round(seconds / 3600)}h`
  if (seconds === 86400) return 'Every 24h'
  return `Every ${Math.round(seconds / 86400)}d`
}

function sourceCadenceLabelFor(nodeId: string): string | null {
  const seconds = sourceCadenceSecondsById.value[nodeId]
  return seconds == null ? null : _formatCadence(seconds)
}

const filterRecordLimit = ref<number | null>(10)
const filterAutoRun = ref(false)
const filterLastRunSummary = ref<string | null>(null)

const prospectRecordLimit = ref<number | null>(1)
const prospectLimitOptions = [
  { label: '1', value: 1 },
  { label: '5', value: 5 },
  { label: '10', value: 10 },
]
const prospectAutoRunMode = ref('off')
const prospectLastRunSummary = ref<string | null>(null)

const actionRecordLimit = ref<number | null>(10)

const sequenceLastRunSummary = ref<string | null>(null)
const dialerLastRunSummary = ref<string | null>(null)

const sequenceAutoApprovePolicy = ref('none')
const sequenceActionId = ref<string | null>(null)
const dialerActionId = ref<string | null>(null)
const workspaceRequiresApproval = ref(false)
const actionPhaseAutoRun = ref(false)
const actionRunCounts = ref<Record<string, Record<string, number>>>({})

// Placeholder run stats — will be wired to real data later
const runStats = ref({
  stagedAccountCount: null as number | null,
  stagedContactCount: null as number | null,
  qualifiedAccountCount: null as number | null,
  qualifiedContactCount: null as number | null,
  activeAccountCount: null as number | null,
  activeContactCount: null as number | null,
  prospectedCount: null as number | null,
})

/** Group resolved nodes for run mode: consecutive filters merge into a
 *  filterGroup, consecutive actions (sequence/dialer) merge into an
 *  actionGroup, consecutive sources merge into a sourceGroup — purely a
 *  visual/label wrapper for sources, since each one runs and auto-runs
 *  fully independently (no shared play-level flag like the other two). */
/** Fixed Find/Qualify/Prospect/Action slots, always present (even empty)
 *  so every phase's box renders in run mode, same as edit mode's groups. */
const runGroups = computed<RunGroup[]>(() => {
  const nodes = resolvedNodes.value
  // A Strategy play chooses people from its brief: only its Actions are on the canvas.
  if (isFlock.value) return [{ kind: 'actionGroup', nodes: nodes.filter((n) => n.kind === 'action') }]
  return [
    { kind: 'sourceGroup', nodes: nodes.filter((n) => n.kind === 'source') },
    { kind: 'filterGroup', nodes: nodes.filter((n) => n.kind === 'filter') },
    { kind: 'single', node: nodes.find((n) => n.kind === 'prospect') || null },
    { kind: 'actionGroup', nodes: nodes.filter((n) => n.kind === 'action') },
  ]
})

/**
 * Gaps between (and around) the 4 fixed run groups (Find, Qualify,
 * Prospect, Action) — always 5 positions, always shown, since every
 * phase box is now always present:
 *   gap[0] = before Find             (nothing flows in yet — not shown)
 *   gap[1] = Find → Qualify          (staged accounts + staged contacts)
 *   gap[2] = Qualify → Prospect       (qualified accounts — what prospecting works from)
 *   gap[3] = Prospect → Action        (qualified contacts — what actions work from)
 *   gap[4] = after Action             (nothing more to show)
 */
interface GapBadgeEntry { icon: 'account' | 'contact'; count: number | null; link: string }
interface GapBadge { show: boolean; entries: GapBadgeEntry[] }

const runGaps = computed<GapBadge[]>(() => {
  if (isFlock.value) return []
  const base = `/sales-plays/${playId.value}/candidates`
  return [
    { show: false, entries: [] },
    { show: true, entries: [
      { icon: 'contact', count: runStats.value.stagedContactCount, link: `${base}/people?stage=staged` },
      { icon: 'account', count: runStats.value.stagedAccountCount, link: `${base}/companies?stage=staged` },
    ] },
    { show: true, entries: [
      { icon: 'account', count: runStats.value.qualifiedAccountCount, link: `${base}/companies?stage=qualified` },
    ] },
    { show: true, entries: [
      { icon: 'contact', count: runStats.value.qualifiedContactCount, link: `${base}/people?stage=qualified` },
    ] },
    { show: false, entries: [] },
  ]
})


function getFilterIntermediateCount(_filterIndex: number): number | null {
  // Per-filter intermediate counts are not yet available from the API.
  return null
}

function onRunNodeClick(node: ResolvedCanvasNode, initialSubTab: string | null = null) {
  const draftNode = draft.nodes.value.find(n => n.id === node.id)
  const nodeIndex = draftNode ? draft.nodes.value.indexOf(draftNode) : 0
  setContext({
    type: CANVAS_CONTEXT_TYPE,
    playId: playId.value,
    pipelineId: draft.pipelineId.value,
    nodeKind: node.kind,
    nodeIndex,
    nodeType: draftNode?.type || '',
    nodeLabel: node.title,
    sourceId: node.kind === 'source' ? (node.detailsProps?.config?.id || null) : null,
    actionId: node.kind === 'action' ? (draft.actionIds.value[draftNode?.type || ''] || null) : null,
    canvasMode: 'run',
    initialSubTab,
  })
  openDrawer()
}

const filterRunning = ref(false)
const prospectRunning = ref(false)
const actionRunning = ref(false)

/** `nodeId` is the canvas node id (`source-${source.id}`); `sourceId` is
 *  the backend source id that node points at. */
async function onRunSource(nodeId: string, sourceId: string | null) {
  if (!sourceId) return
  sourceRunningById.value = { ...sourceRunningById.value, [nodeId]: true }
  try {
    await apiFetch(`/sales-plays/${playId.value}/sources/${sourceId}/run`, {
      method: 'POST',
      body: { record_limit: sourceRecordLimitFor(nodeId) },
    })
    await fetchPlay(true)
    await fetchRuns()
  } finally {
    sourceRunningById.value = { ...sourceRunningById.value, [nodeId]: false }
  }
}

async function onRunFilters() {
  filterRunning.value = true
  try {
    await apiFetch(`/sales-plays/${playId.value}/filters/run`, {
      method: 'POST',
      body: { record_limit: filterRecordLimit.value },
    })
    await fetchPlay(true)
    await fetchRuns()
  } finally {
    filterRunning.value = false
  }
}

async function onRunProspect() {
  prospectRunning.value = true
  try {
    await apiFetch(`/sales-plays/${playId.value}/prospect/run`, {
      method: 'POST',
      body: { record_limit: prospectRecordLimit.value },
    })
    await fetchPlay(true)
    await fetchRuns()
  } finally {
    prospectRunning.value = false
  }
}

async function onRunActions() {
  actionRunning.value = true
  try {
    await apiFetch(`/sales-plays/${playId.value}/actions/run`, {
      method: 'POST',
      body: { record_limit: actionRecordLimit.value },
    })
    await fetchPlay(true)
    await fetchRuns()
  } finally {
    actionRunning.value = false
  }
}

async function onToggleSourceAutoRun(nodeId: string, sourceId: string | null, enabled: boolean) {
  if (!sourceId) return
  sourceAutoRunById.value = { ...sourceAutoRunById.value, [nodeId]: enabled }
  await apiFetch(`/sales-plays/${playId.value}/sources/${sourceId}`, {
    method: 'PATCH',
    body: { status: enabled ? 'active' : 'paused' },
  })
}

async function onToggleFilterAutoRun(enabled: boolean) {
  filterAutoRun.value = enabled
  await apiFetch(`/sales-plays/${playId.value}`, {
    method: 'PATCH',
    body: { update: { config: { auto_approval_policy: enabled ? 'auto_if_match' : 'manual' } } },
  })
}

async function onSetProspectAutoRunMode(mode: string) {
  prospectAutoRunMode.value = mode
  await apiFetch(`/sales-plays/${playId.value}`, {
    method: 'PATCH',
    body: { update: { config: { auto_run_prospecting: mode } } },
  })
}

async function onChangeSequenceAutoApprovePolicy(policy: string) {
  if (!sequenceActionId.value) return
  sequenceAutoApprovePolicy.value = policy
  await apiFetch(`/sales-plays/${playId.value}`, {
    method: 'PATCH',
    body: {
      update: {
        actions: [{
          action_type: 'sequence',
          action_id: sequenceActionId.value,
          agent: { auto_approve_email: policy },
        }],
      },
    },
  })
}

async function onToggleActionPhaseAutoRun(enabled: boolean) {
  actionPhaseAutoRun.value = enabled
  await apiFetch(`/sales-plays/${playId.value}`, {
    method: 'PATCH',
    body: { update: { config: { auto_run_action_phase: enabled } } },
  })
}

// ── Run history ─────────────────────────────────────────────────

const playRuns = ref<any[]>([])

function latestSourceRunFor(sourceId: string | null) {
  if (!sourceId) return null
  const r = playRuns.value.find((r: any) => r.execution_mode === 'source' && r.source_id === sourceId)
  return r ? { id: r.id, status: r.status } : null
}

const latestFilterRun = computed(() => {
  const r = playRuns.value.find((r: any) => r.execution_mode === 'filter')
  return r ? { id: r.id, status: r.status } : null
})

const filterRunCounts = computed(() => {
  const runs = playRuns.value.filter((r: any) => r.execution_mode === 'filter')
  const runningRuns = runs.filter((r: any) => r.status === 'running')
  const queuedRuns = runs.filter((r: any) => r.status === 'queued')
  return {
    running: runningRuns.reduce((sum: number, r: any) => sum + (r.candidates_discovered || 0), 0),
    queued: queuedRuns.reduce((sum: number, r: any) => sum + (r.candidates_discovered || 0), 0),
  }
})

const prospectRunCounts = computed(() => {
  const runs = playRuns.value.filter((r: any) => r.execution_mode === 'prospect')
  return {
    running: runs.filter((r: any) => r.status === 'running').length,
    queued: runs.filter((r: any) => r.status === 'queued').length,
    latest: runs[0] || null,
  }
})

const sequenceRunCounts = computed(() => {
  // Show legacy "agent" runs with sequence runs
  const runs = playRuns.value.filter((r: any) =>
    r.execution_mode === 'sequence' || r.execution_mode === 'agent'
  )
  return {
    running: runs.filter((r: any) => r.status === 'running').length,
    queued: runs.filter((r: any) => r.status === 'queued').length,
    latest: runs[0] || null,
  }
})

const dialerRunCounts = computed(() => {
  // One dialer_acceptance AgentRun batches many candidates — show candidate
  // count, not job count, same convention as filterRunCounts.
  const runs = playRuns.value.filter((r: any) => r.execution_mode === 'dialer_acceptance')
  const runningRuns = runs.filter((r: any) => r.status === 'running')
  const queuedRuns = runs.filter((r: any) => r.status === 'queued')
  return {
    running: runningRuns.reduce((sum: number, r: any) => sum + (r.candidates_discovered || 0), 0),
    queued: queuedRuns.reduce((sum: number, r: any) => sum + (r.candidates_discovered || 0), 0),
    latest: runs[0] || null,
  }
})

async function fetchRuns() {
  try {
    const resp: any = await apiFetch(`/sales-plays/${playId.value}/runs`)
    playRuns.value = resp?.runs || []
    hydrateLastRunSummaries()
  } catch { /* silent */ }
}

async function fetchActionRunCounts() {
  const ids = [sequenceActionId.value, dialerActionId.value].filter((id): id is string => !!id)
  const next: Record<string, Record<string, number>> = {}
  await Promise.all(ids.map(async (id) => {
    try {
      const resp: any = await apiFetch(`/sales-plays/${playId.value}/actions/${id}/action-runs/status-counts`)
      next[id] = resp?.counts || {}
    } catch { /* silent — the footer just shows no counts */ }
  }))
  actionRunCounts.value = next
}

function actionInProgressCount(actionId: string | null): number {
  if (!actionId) return 0
  return actionRunCounts.value[actionId]?.in_progress || 0
}

function sourceLastRunSummaryFor(sourceId: string | null): string | null {
  if (!sourceId) return null
  const run = playRuns.value.find((r: any) => r.execution_mode === 'source' && r.source_id === sourceId)
  if (!run) return null
  const date = run.created_at ? formatRunDate(run.created_at) : ''
  return `Last run: ${date}`
}

function hydrateLastRunSummaries() {
  const filterRun = playRuns.value.find((r: any) => r.execution_mode === 'filter')
  if (filterRun) {
    const date = filterRun.created_at ? formatRunDate(filterRun.created_at) : ''
    const summary = filterRun.status_summary || ''
    filterLastRunSummary.value = `Last run: ${date}${summary ? ' \u00B7 ' + summary : ''}`
  }

  const prospectRun = playRuns.value.find((r: any) => r.execution_mode === 'prospect')
  if (prospectRun) {
    const date = prospectRun.created_at ? formatRunDate(prospectRun.created_at) : ''
    prospectLastRunSummary.value = `Last run: ${date}`
  }
}

function formatRunDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    + ' \u00B7 '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

// ── Canvas node state (driven by draft) ─────────────────────────

const selectedNodeIndex = ref<number | null>(null)

/** The open per-group add-menu, if any — `insertAt: null` means "append
 *  to the end of this group" rather than a between-card insertion. */
const addMenuOpenFor = ref<{ kind: CanvasNodeKind; insertAt: number | null; side: 'left' | 'right' } | null>(null)
const addBtnRefs = ref<Record<string, HTMLElement | null>>({})
const addMenuRefs = ref<Record<string, HTMLElement | null>>({})

const dirty = draft.dirty
const saving = draft.saving

/** Pre-resolved nodes — template doesn't know about the registry. */
const resolvedNodes = computed(() => resolveCanvasNodes(draft.nodes.value))

const EDIT_GROUP_ORDER: { kind: CanvasNodeKind; label: string }[] = [
  { kind: 'source', label: 'Find' },
  { kind: 'filter', label: 'Qualify' },
  { kind: 'prospect', label: 'Prospect' },
  { kind: 'action', label: 'Action' },
]

/** Edit mode's fixed 4 group boxes — always present (even empty) so the
 *  add-menu can be scoped per-group instead of one global insertion index. */
const editGroups = computed<EditGroup[]>(() => {
  const nodes = draft.nodes.value
  const resolved = resolvedNodes.value
  return EDIT_GROUP_ORDER.filter(({ kind }) => !isFlock.value || kind === 'action').map(({ kind, label }) => ({
    kind,
    label,
    nodes: nodes
      .map((n, index) => ({ node: resolved[index], index }))
      .filter((entry) => entry.node && nodes[entry.index].kind === kind),
  }))
})

/** Available node types for a given group's add-menu — scoped to that
 *  group's kind, since the group box itself fixes what can go inside it. */
function addMenuItemsFor(kind: CanvasNodeKind) {
  const items = availableCanvasNodeDefsForKind(kind, draft.nodes.value)
  return isFlock.value ? items.filter((def) => def.type === 'sequence') : items
}

/** Same items as addMenuItemsFor, but split into labeled sections for the
 *  Qualify group — company-level vs. person-level filters — so it's clear
 *  which kind of record a filter evaluates. Other kinds get one unlabeled
 *  section (unchanged behavior). */
function addMenuSectionsFor(kind: CanvasNodeKind): { label: string | null; items: CanvasNodeDefinition[] }[] {
  const items = addMenuItemsFor(kind)
  if (kind !== 'filter') return [{ label: null, items }]
  const companies = items.filter((def) => def.operatesOn === 'company')
  const people = items.filter((def) => def.operatesOn === 'person')
  const other = items.filter((def) => def.operatesOn !== 'company' && def.operatesOn !== 'person')
  const sections: { label: string | null; items: CanvasNodeDefinition[] }[] = []
  if (companies.length > 0) sections.push({ label: 'Qualify Companies', items: companies })
  if (people.length > 0) sections.push({ label: 'Qualify People', items: people })
  if (other.length > 0) sections.push({ label: null, items: other })
  return sections
}

const ADD_MENU_WIDTH = 380
const ADD_MENU_GAP = 12

/** The menu opens beside its (+) on whichever side has room: with the step
 *  drawer open the canvas is too narrow for it on the right. */
function openAddMenuFor(kind: CanvasNodeKind, insertAt: number | null) {
  const button = addBtnRefs.value[`${kind}:end`]
  const bounds = button?.closest('.canvasBg')?.getBoundingClientRect()
  const right = button?.getBoundingClientRect().right ?? 0
  const fits = !bounds || right + ADD_MENU_GAP + ADD_MENU_WIDTH <= bounds.right
  addMenuOpenFor.value = { kind, insertAt, side: fits ? 'right' : 'left' }
}

function closeAddMenu() {
  addMenuOpenFor.value = null
}

function addNodeToGroup(def: CanvasNodeDefinition, insertAt: number | null) {
  addMenuOpenFor.value = null
  const index = draft.addNode(def, insertAt)
  // Default the prospect node's name, personas, and contact cap from the play.
  if (def.kind === 'prospect' && play.value) {
    const p = play.value as any
    const existingPersonas = [...(p.personas?.local || []), ...(p.personas?.referenced || [])]
    const patch: Record<string, any> = { max_contacts_per_account: 5 }
    if (p.name) patch.name = p.name
    if (existingPersonas.length > 0) patch.personas = existingPersonas
    draft.updateNodeConfig(index, patch)
  }
  // Default a newly-added ICP node from the play's existing ICP
  if (def.type === 'icp' && play.value) {
    const existingIcp = (play.value as any).icp?.config
    if (existingIcp && Object.keys(existingIcp).length > 0) {
      draft.updateNodeConfig(index, { icp: existingIcp })
    }
  }
  nextTick(() => selectNode(index))
}

function removeNode(index: number) {
  draft.removeNode(index)
  if (selectedNodeIndex.value === index) {
    selectedNodeIndex.value = null
    setContext(null)
  } else if (selectedNodeIndex.value !== null && selectedNodeIndex.value > index) {
    selectedNodeIndex.value--
  }
}

function moveNode(index: number, direction: 'up' | 'down') {
  draft.moveNode(index, direction)
  if (selectedNodeIndex.value === index) {
    selectedNodeIndex.value = direction === 'up' ? index - 1 : index + 1
  } else if (selectedNodeIndex.value === (direction === 'up' ? index - 1 : index + 1)) {
    selectedNodeIndex.value = index
  }
}

function selectNode(index: number) {
  const node = draft.nodes.value[index]
  if (!node) return
  selectedNodeIndex.value = index
  const def = getCanvasNodeDef(node.type)
  const label = node.config.report_name || node.config.name || def?.addMenu.label || 'Untitled'
  setContext({
    type: CANVAS_CONTEXT_TYPE,
    playId: playId.value,
    pipelineId: draft.pipelineId.value,
    nodeKind: node.kind,
    nodeIndex: index,
    nodeType: node.type,
    nodeLabel: label,
    canvasMode: 'edit',
  })
  openDrawer()
}

function deselectNode() {
  selectedNodeIndex.value = null
  setContext(null)
}

function onRenameNode(index: number, name: string) {
  draft.updateNodeConfig(index, { name })
}

function isUnnamedNode(node: { kind: string; actionType?: string }): boolean {
  return node.kind === 'prospect' || (node.kind === 'action' && node.actionType === 'dialer')
}

const sequenceImpactOpen = ref(false)
const sequenceImpactCounts = ref<{ affectedCount: number; unaffectedCount: number } | null>(null)
const sequenceImpactBusy = ref(false)
const previewError = ref('')
const previewing = ref(false)

async function onSave() {
  previewError.value = ''
  previewing.value = true
  let impact: { affectedCount: number; unaffectedCount: number } | null = null
  try {
    impact = await draft.previewSequenceImpact()
  } catch (e: any) {
    // Preview failing shouldn't silently fall through to an unconfirmed
    // save — surface it and stop, same as any other save error would.
    previewError.value = e?.message || 'Failed to preview sequence changes'
    return
  } finally {
    previewing.value = false
  }
  if (impact && impact.affectedCount > 0) {
    sequenceImpactCounts.value = impact
    sequenceImpactOpen.value = true
    return
  }
  await finishSave('exit')
}

async function finishSave(disposition: 'exit' | 'exit_and_reenroll') {
  const ok = await draft.save(disposition)
  if (ok) {
    mode.value = 'run'
    selectedNodeIndex.value = null
    setContext(null)
  }
}

async function onConfirmSequenceImpact(disposition: 'exit' | 'exit_and_reenroll') {
  sequenceImpactBusy.value = true
  try {
    await finishSave(disposition)
  } finally {
    sequenceImpactBusy.value = false
    sequenceImpactOpen.value = false
    sequenceImpactCounts.value = null
  }
}

function onCancelSequenceImpact() {
  sequenceImpactOpen.value = false
  sequenceImpactCounts.value = null
}

function onCancel() {
  draft.cancel()
  mode.value = 'run'
  selectedNodeIndex.value = null
  setContext(null)
}

// Close add menu on outside click.
function onDocumentClick(e: MouseEvent) {
  if (!addMenuOpenFor.value) return
  const target = e.target as Node
  for (const el of [...Object.values(addBtnRefs.value), ...Object.values(addMenuRefs.value)]) {
    if (el && (el as HTMLElement).contains?.(target)) return
  }
  closeAddMenu()
}

function onDocumentKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && addMenuOpenFor.value) {
    closeAddMenu()
  }
}

// ── Data fetching ───────────────────────────────────────────────

let pollHandle: number | undefined

async function hydrateDraftFromPlay(refresh = false) {
  const p = play.value as any
  if (!p) return
  const existingNodes: CanvasNode[] = []

  const sources = p?.sources || []
  const pipeline = p?.pipelines?.[0]
  if (sources.length > 0) {
    for (const source of sources) {
      // Unwrap envelope: {type, display_name, definition} → inner definition.
      const srcEnvelope = source.definition || {}
      const srcInner = srcEnvelope.definition || srcEnvelope
      const srcName = srcEnvelope.display_name || ''
      existingNodes.push({
        id: `source-${source.id}`,
        type: source.source_type,
        kind: 'source',
        config: { id: source.id, name: srcName, ...srcInner },
      })
    }
  } else if (pipeline?.audience_definition?.produce) {
    const produce = pipeline.audience_definition.produce
    existingNodes.push({
      id: `source-${produce.type}-${Date.now()}`,
      type: produce.type,
      kind: 'source',
      config: { ...produce },
    })
  }

  const filters = p?.filters || []
  for (const f of filters) {
    // Unwrap envelope: {type, display_name, definition} → inner definition.
    const envelope = f.definition || {}
    const innerDef = envelope.definition || envelope
    const displayName = envelope.display_name || ''
    existingNodes.push({
      id: `filter-${f.filter_type}-${Date.now()}`,
      type: f.filter_type,
      kind: 'filter',
      config: f.filter_type === 'icp'
        ? (() => {
            const { fail_on_missing_data, mode, ...icpCriteria } = innerDef || {}
            return {
              id: f.id,
              name: displayName || 'Ideal Customer Profile',
              icp: icpCriteria,
              fail_on_missing_data: fail_on_missing_data !== false,
              mode: mode || 'keep_matching',
            }
          })()
        : { id: f.id, name: displayName, ...innerDef },
    })
  }

  // Prospect node: the play's prospecting_agent config, if any. It carries
  // no name of its own — the play's own name/identity stands in for it.
  if (p?.prospecting_agent) {
    const allPersonas = [...(p.personas?.local || []), ...(p.personas?.referenced || [])]
    existingNodes.push({
      id: `prospect-${Date.now()}`,
      type: 'prospect',
      kind: 'prospect',
      config: {
        approach: p.prospecting_agent.approach || '',
        personas: allPersonas,
        max_contacts_per_account: p.prospecting_agent.max_contacts_per_account ?? null,
        active_days: p.prospecting_agent.cadence_config.active_days,
        opportunistic_days: p.prospecting_agent.cadence_config.opportunistic_days,
      },
    })
  }

  // Actions: at most one per action_type. A sequence action's agent +
  // sequence + sample emails are already resolved in its `definition` —
  // no follow-up fetch needed. Step-level run stats (ready/waiting counts,
  // exit counts) aren't in the definition, so those still come from
  // /sequences/stats.
  const actions: any[] = p?.actions || []
  const actionIds: Record<string, string> = {}
  let sequenceAgentId: string | null = null
  let sequenceId: string | null = null

  const sequenceAction = actions.find((a) => a.action_type === 'sequence')
  if (sequenceAction) {
    actionIds.sequence = sequenceAction.id
    const def = sequenceAction.definition || {}
    sequenceAgentId = def.agent?.id || null
    sequenceId = def.contact_sequence_id || null

    let seqStats: any = null
    if (sequenceId) {
      try {
        const statsResp: any = await apiFetch('/sequences/stats')
        const allStats = statsResp.stats || []
        seqStats = allStats.find((s: any) => String(s.sequence_id) === String(sequenceId)) || null
      } catch { /* stats are best-effort */ }
    }

    existingNodes.push({
      id: `sequence-${Date.now()}`,
      type: 'sequence',
      kind: 'action',
      actionType: 'sequence',
      config: {
        name: def.sequence?.name || '',
        sequence_id: sequenceId,
        sequence_description: def.sequence?.description || '',
        steps: (def.sequence?.steps || []).map((s: any) => ({
          step_index: s.step_index,
          step_type: s.step_type,
          brief: s.brief || '',
          wait_days: s.wait_days || null,
          wait_unit: s.wait_unit || 'days',
        })),
        sender_approach: def.agent?.approach || '',
        sample_emails: def.sample_emails || [],
        scheduling_customized: !!def.sequence?.scheduling_override,
        scheduling_timezone_aware_sending: def.sequence?.scheduling_override?.timezone_aware_sending || false,
        scheduling_default_us_timezone: def.sequence?.scheduling_override?.default_us_timezone || '',
        scheduling_default_send_timezone: def.sequence?.scheduling_override?.default_send_timezone || '',
        sequence_step_stats: seqStats?.step_stats || [],
        pending_exit_count: seqStats?.pending_exit_count || 0,
        exited_completed: seqStats?.exited_completed || 0,
        exited_timed_out: seqStats?.exited_timed_out || 0,
        exited_removed: seqStats?.exited_removed || 0,
      },
    })
  }

  const dialerAction = actions.find((a) => a.action_type === 'dialer')
  if (dialerAction) {
    actionIds.dialer = dialerAction.id
    let dialCounts: Record<string, number> = { '0': 0, '1': 0, '2': 0, '3+': 0 }
    try {
      const distResp: any = await apiFetch(
        `/dialer/queue/dial-count-distribution?sales_play_id=${encodeURIComponent(playId.value)}`,
      )
      dialCounts = distResp?.counts || dialCounts
    } catch { /* counts are best-effort */ }

    existingNodes.push({
      id: `dialer-${Date.now()}`,
      type: 'dialer',
      kind: 'action',
      actionType: 'dialer',
      config: { dial_counts: dialCounts },
    })
  }

  const opts = {
    pipelineId: pipeline?.id || null,
    sequenceAgentId,
    sequenceId,
    actionIds,
    stepTypes: isFlock.value ? STRATEGY_STEP_TYPES : null,
  }
  if (refresh) {
    draft.refreshFromBackend(existingNodes, opts)
  } else {
    draft.initialize(playId.value, opts, existingNodes)
  }
}

async function fetchPlay(silent = false) {
  if (!silent) {
    loading.value = true
    error.value = ''
  }
  try {
    const resp: any = await apiFetch(`/sales-plays/${playId.value}`)
    play.value = resp?.play || null
    if (silent) error.value = ''

    // Hydrate run mode settings from play data
    const p = resp?.play as any
    if (p) {
      const sequenceAction = (p.actions || []).find((a: any) => a.action_type === 'sequence')
      sequenceActionId.value = sequenceAction?.id || null
      sequenceAutoApprovePolicy.value = sequenceAction?.definition?.agent?.auto_approve_email || 'none'
      const dialerAction = (p.actions || []).find((a: any) => a.action_type === 'dialer')
      dialerActionId.value = dialerAction?.id || null
      void fetchActionRunCounts()
      const nextSourceAutoRun: Record<string, boolean> = {}
      const nextSourceCadenceSeconds: Record<string, number | null> = {}
      for (const source of (p.sources || [])) {
        const nodeId = `source-${source.id}`
        nextSourceAutoRun[nodeId] = source.status === 'active'
        nextSourceCadenceSeconds[nodeId] = source.cadence_seconds ?? null
      }
      sourceAutoRunById.value = nextSourceAutoRun
      sourceCadenceSecondsById.value = nextSourceCadenceSeconds
      filterAutoRun.value = p.config?.auto_approval_policy !== 'manual'
      prospectAutoRunMode.value = p.config?.auto_run_prospecting || 'off'
      actionPhaseAutoRun.value = !!p.config?.auto_run_action_phase
      runStats.value = {
        stagedAccountCount: p.staged_account_count ?? null,
        stagedContactCount: p.staged_contact_count ?? null,
        qualifiedAccountCount: p.qualified_account_count ?? null,
        qualifiedContactCount: p.qualified_contact_count ?? null,
        activeAccountCount: p.enrolled_account_count ?? null,
        activeContactCount: p.enrolled_contact_count ?? null,
        prospectedCount: p.active_count ?? null,
      }
      // Re-hydrate draft nodes from polled data when in run mode
      // (not editing, so no unsaved changes to lose).
      if (silent && mode.value === 'run' && !dirty.value) {
        await hydrateDraftFromPlay(true)
      }
    }
  } catch (e: any) {
    if (!silent) error.value = e?.message || 'Failed to load play'
  } finally {
    if (!silent) loading.value = false
  }
}

async function loadEmailSettings() {
  try {
    const resp: any = await apiFetch('/settings/email')
    workspaceRequiresApproval.value = !!resp?.require_approval
  } catch {
    // not critical
  }
}

onMounted(async () => {
  await initBlockRegistry()
  setScope({
    scopeType: 'sales_play',
    scopeSubjectId: playId.value,
    pageContext: { sales_play_id: playId.value, page_type: 'canvas' },
  })
  document.addEventListener('click', onDocumentClick, true)
  document.addEventListener('keydown', onDocumentKeydown)
  await fetchPlay(false)
  await hydrateDraftFromPlay()
  await fetchRuns()
  await loadEmailSettings()
  pollHandle = window.setInterval(() => { fetchPlay(true); fetchRuns() }, 5000)
})

onBeforeUnmount(() => {
  if (pollHandle !== undefined) window.clearInterval(pollHandle)
  document.removeEventListener('click', onDocumentClick, true)
  document.removeEventListener('keydown', onDocumentKeydown)
  clearScope()
})
</script>

<style scoped>
/* ─── Mode bar ─── */
.modeBar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  padding: var(--space-4) 0 0;
}
.modeBar a.btn {
  text-decoration: none;
}

.saveErrorBanner {
  max-width: 540px;
  margin: var(--space-2) auto 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-danger, #b91c1c);
  text-align: right;
}

.sequenceImpactText {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-normal);
  color: var(--color-text-secondary);
  margin: 0;
}

.cornerCount {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  text-decoration: none;
}

.cornerCount--link {
  cursor: pointer;
}
.cornerCount--link:hover {
  color: var(--color-accent);
}

/* ─── Canvas flow ─── */
.canvasBg {
  background:
    radial-gradient(circle, rgba(0, 0, 0, 0.08) 1.5px, transparent 1.5px);
  background-size: 24px 24px;
  background-position: 12px 12px;
  margin: 0 calc(-1 * var(--space-8));
  padding: 0 var(--space-8);
}

.canvasFlow {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-6) var(--space-6) var(--space-8);
  max-width: 540px;
  margin: 0 auto;
  overflow: visible;
}

.connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}
.connector::before,
.connector::after {
  content: '';
  width: 2px;
  height: 14px;
  background: var(--color-border-strong);
  border-radius: 1px;
}

/* Trailing connector: only a line above the badge, no line below */
.connectorTrailing {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}
.connectorTrailing::before {
  content: '';
  width: 2px;
  height: 14px;
  background: var(--color-border-strong);
  border-radius: 1px;
}
.connectorInsertBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 2px dashed var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-bg-surface);
  color: var(--color-text-tertiary);
  cursor: pointer;
  box-shadow: var(--shadow-neu-out-cream-sm);
  transition: border-color var(--duration-fast) var(--ease-out),
              color var(--duration-fast) var(--ease-out),
              box-shadow var(--duration-fast) var(--ease-out);
}
.connectorInsertBtn:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
  box-shadow: var(--shadow-neu-out-cream);
}

.startPill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2) var(--space-5);
  border-radius: var(--radius-full);
  background: var(--color-accent);
  color: white;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: lowercase;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
}

/* Empty phase box in run mode: solid border (unlike edit mode's dashed
   empty state), with a short hint about the phase's purpose. */
.emptyGroupHint {
  margin: var(--space-6) auto;
  margin-top: calc(var(--space-6) - var(--filter-group-label-height));
  max-width: 220px;
  text-align: center;
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  line-height: var(--lh-normal);
  color: var(--color-text-tertiary);
}

/* ─── Add node button + popover ─── */
.addNodeWrap {
  position: relative;
}
/* Empty group box: center the (+) rather than sitting flush under the label. */
.addNodeWrap.addNodeWrap__centered {
  margin: var(--space-6) 0;
  margin-top: calc(var(--space-6) - var(--filter-group-label-height));
}
.addNodeBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 2px dashed var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-bg-surface);
  color: var(--color-text-tertiary);
  cursor: pointer;
  box-shadow: var(--shadow-neu-out-cream-sm);
  transition: border-color var(--duration-fast) var(--ease-out),
              color var(--duration-fast) var(--ease-out),
              box-shadow var(--duration-fast) var(--ease-out);
}
.addNodeBtn:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
  box-shadow: var(--shadow-neu-out-cream);
}

.addMenu {
  position: absolute;
  top: calc(100% + var(--space-2));
  left: 50%;
  transform: translateX(-50%);
  width: 380px;
  background: var(--color-bg-surface);
  border: 2px solid var(--color-border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0,0,0,.15));
  padding: var(--space-3);
  z-index: 10;
}
.addMenu--right,
.addMenu--left {
  top: 50%;
  transform: translateY(-50%);
  max-height: 60vh;
  overflow-y: auto;
}
.addMenu--right {
  left: calc(100% + var(--space-3));
}
.addMenu--left {
  left: auto;
  right: calc(100% + var(--space-3));
}
.addMenuCategory {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
  padding: var(--space-2) var(--space-2) var(--space-1);
}
.addMenuCategory:not(:first-child) {
  margin-top: var(--space-2);
  border-top: 1px solid var(--color-border-subtle);
  padding-top: var(--space-3);
}
.addMenuEmpty {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
  padding: var(--space-2);
}

.addMenuItem {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2);
  border: none;
  border-radius: var(--radius-md);
  background: none;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: background var(--duration-fast) var(--ease-out);
}
.addMenuItem:hover {
  background: var(--color-bg-subtle);
}

.addMenuItemIcon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-surface);
  color: var(--color-text-primary);
  box-shadow: var(--shadow-neu-out-cream-sm);
}

.addMenuItemText {
  flex: 1;
  min-width: 0;
}
.addMenuItemTitle {
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.addMenuItemDesc {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
  line-height: var(--lh-normal);
}

.loading, .error {
  margin-top: var(--space-7);
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  color: var(--color-text-secondary);
}
</style>
